import React, { useState } from 'react';
import {
	Box,
	Button,
	Chip,
	Divider,
	IconButton,
	ListItemIcon,
	Menu,
	MenuItem,
	Switch,
	Tooltip,
	Typography
} from '@mui/material';
import { CustomizedDataGrid } from '../customized_data_grid';
import DialogueModal from 'components/Modal/DialogModal';
import { Column } from 'react-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteConfirmationDialog from './DeleteModal';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ChannelRow } from './createAlert';
import ChannelModal from './ChannelModal';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTheme } from '@mui/material/styles';

const ActionCell = ({
  row,
  channels,
  setChannels,
  setSelectedChannel,
  setShowChannelModal
}: any) => {
  const theme = useTheme();
  const colors = theme.palette;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState('');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleStatus = () => {
    const channel = channels[row.channel];
    setChannels({
      ...channels,
      [row.channel]: { ...channel, isEnabled: !row.isEnabled }
    });
    handleClose();
  };

  const handleDeleteClick = () => {
    setChannelToDelete(row.channel);
    setOpenDialog(true);
    handleClose();
  };

  const handleDeleteConfirm = () => {
    const updatedChannels = { ...channels };
    delete updatedChannels[channelToDelete];
    setChannels(updatedChannels);
    setOpenDialog(false);
  };

  return (
    <>
      {/* Action Buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          aria-label="edit"
          size="small"
          onClick={() => {
            setSelectedChannel(channels[row.channel]);
            setShowChannelModal(true);
          }}
          sx={{ color: `${colors.table.moreIcon} !important` }}
        >
          <EditIcon
            fontSize="small"
            sx={{ color: `${colors.table.moreIcon} !important` }}
          />
        </IconButton>

        <IconButton
          aria-label="more"
          size="small"
          onClick={handleClick}
          sx={{ color: `${colors.table.moreIcon} !important` }}
        >
          <MoreVertIcon
            fontSize="small"
            sx={{ color: `${colors.table.moreIcon} !important` }}
          />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            paper: {
              sx: {
                '& .MuiMenu-list': {
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              },
            },
          }}
          sx={{
            '& .MuiPaper-root': {
              backgroundColor: `${colors.moreOptionDropdown?.background} !important`,
              color: `${colors.moreOptionDropdown?.menuItemText} !important`,
              borderRadius: '6px',
              boxShadow: `
                -2px 0 6px rgba(0, 0, 0, 0.2),
                2px 0 6px rgba(0, 0, 0, 0.2),
                0 4px 8px rgba(0, 0, 0, 0.25)
              `,
              minWidth: 160,
            },
          }}
        >
          <MenuItem
            onClick={handleToggleStatus}
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: `${colors.moreOptionDropdown?.menuItemText} !important`,
              minHeight: '48px !important',
              py: 0,
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: `${colors.moreOptionDropdown?.menuItemHover} !important`,
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 32,
              }}
            >
              <Switch
                checked={row.isEnabled}
                size="small"
                color="default"
                sx={{
                  pointerEvents: 'none',
                  width: 34,
                  height: 20,
                  padding: 0,

                  '& .MuiSwitch-switchBase': {
                    padding: '3px',
                    color: colors.toggle.thumb,

                    '&.Mui-checked': {
                      transform: 'translateX(14px)',
                      color: colors.toggle.thumb,

                      '& + .MuiSwitch-track': {
                        backgroundColor: colors.toggle.trackOn,
                        opacity: 1
                      }
                    },

                    '&.Mui-disabled + .MuiSwitch-track': {
                      backgroundColor: colors.toggle.disabledTrack,
                      opacity: 1
                    }
                  },

                  '& .MuiSwitch-thumb': {
                    width: 14,
                    height: 14,
                    backgroundColor: colors.toggle.thumb,
                    boxShadow: colors.toggle.thumbShadow
                  },

                  '& .MuiSwitch-track': {
                    borderRadius: '10px',
                    backgroundColor: colors.toggle.trackOff,
                    opacity: 1
                  }
                }}
              />
            </ListItemIcon>

            <Box component="span">
              {row.isEnabled ? 'Deactivate' : 'Activate'}
            </Box>
          </MenuItem>

          <Divider
            sx={{
              margin: '0 !important',
              backgroundColor: `${colors.widget.text.noChange}`,
            }}
          />

          <MenuItem
            onClick={handleDeleteClick}
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: `${colors.moreOptionDropdown?.deleteMenuItem} !important`,
              minHeight: '48px !important',
              py: 0,
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: `${colors.moreOptionDropdown?.menuItemHover} !important`,
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 32,
              }}
            >
              <DeleteOutlineIcon
                sx={{
                  fontSize: '22px',
                  color: `${colors.moreOptionDropdown?.deleteMenuItem} !important`,
                }}
              />
            </ListItemIcon>

            <Box
              component="span"
              sx={{
                fontSize: '14px',
                color: `${colors.moreOptionDropdown?.deleteMenuItem} !important`,
              }}
            >
              Delete
            </Box>
          </MenuItem>
        </Menu>
      </Box>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onConfirm={handleDeleteConfirm}
        silenceName={channelToDelete}
      />
    </>
  );
};

const actionFormatter =
  (
    channels: any,
    setChannels: any,
    setSelectedChannel: any,
    setShowChannelModal: any
  ) =>
  ({ row }: any) =>
    (
      <ActionCell
        row={row}
        channels={channels}
        setChannels={setChannels}
        setSelectedChannel={setSelectedChannel}
        setShowChannelModal={setShowChannelModal}
      />
    );


  const getRecipients = (row: any): string[] => {
  if (Array.isArray(row.emailRecipients)) {
    return row.emailRecipients;
  }

  if (row.teamsMail) {
    return [row.teamsMail];
  }

  return [];
};

const isExternalLink = (value: string) =>
  value.startsWith('http://') || value.startsWith('https://');

const RecipientCell = ({ row }: any) => {
  const theme = useTheme();
  const colors = theme.palette;
  const [showAllRecipients, setShowAllRecipients] = useState(false);

  const recipients = getRecipients(row);

  const visibleRecipients = showAllRecipients
    ? recipients
    : recipients.slice(0, 2);

  const remainingCount = Math.max(recipients.length - 2, 0);

  const handleShowAll = () => {
    setShowAllRecipients(true);
  };

  const handleShowLess = () => {
    setShowAllRecipients(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        maxWidth: '100%',
        flexWrap: 'nowrap',
        overflowX: showAllRecipients ? 'auto' : 'hidden',
        overflowY: 'hidden',
        pb: showAllRecipients ? '4px' : 0,

        '&::-webkit-scrollbar': {
          height: '4px',
        },

        '&::-webkit-scrollbar-thumb': {
          backgroundColor: colors.table.chipBackground,
          borderRadius: '4px',
        },
      }}
    >
      {visibleRecipients.map((recipient: string) => {
        if (isExternalLink(recipient)) {
          return (
            <Tooltip
              key={recipient}
              title={recipient}
              arrow
              placement="top"
            >
              <Box
                component="a"
                href={recipient}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  maxWidth: '360px',
                  flexShrink: 0,
                  color: `${colors.textLight.secondary} !important`,
                  textDecoration: 'none',

                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    maxWidth: '330px',
                    fontSize: '14px',
                    color: `${colors.textLight.secondary} !important`,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {recipient}
                </Typography>

                <OpenInNewIcon
                  sx={{
                    fontSize: '18px',
                    color: `${colors.textLight.secondary} !important`,
                    flexShrink: 0,
                  }}
                />
              </Box>
            </Tooltip>
          );
        }

        return (
          <Tooltip
            key={recipient}
            title={recipient}
            arrow
            placement="top"
          >
            <Chip
              label={recipient}
              size="small"
              sx={{
                width: '220px',
                minWidth: '220px',
                maxWidth: '220px',
                flexShrink: 0,
                height: '28px',
                backgroundColor: `${colors.table.chipBackground} !important`,
                color: `${colors.table.chipText} !important`,
                borderRadius: '14px',

                '& .MuiChip-label': {
                  display: 'block',
                  width: '100%',
                  px: '10px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: `${colors.table.chipText} !important`,
                },
              }}
            />
          </Tooltip>
        )})}

      {!showAllRecipients && remainingCount > 0 && (
        <Button
          type="button"
          variant="text"
          disableRipple
          onClick={handleShowAll}
          sx={{
            minWidth: 'auto',
            flexShrink: 0,
            p: 0,
            fontSize: '14px',
            fontWeight: 400,
            textTransform: 'none',
            color: `${colors.table.moreIcon} !important`,

            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          +{remainingCount} more...
        </Button>
      )}

      {showAllRecipients && recipients.length > 2 && (
        <Button
          type="button"
          variant="text"
          disableRipple
          onClick={handleShowLess}
          sx={{
            minWidth: 'auto',
            flexShrink: 0,
            p: 0,
            fontSize: '14px',
            fontWeight: 400,
            textTransform: 'none',
            color: `${colors.table.moreIcon} !important`,

            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          Show less
        </Button>
      )}
    </Box>
  );
};

export const recipientFormatter =
  () =>
  ({ row }: any) => (
    <RecipientCell row={row} />
  );


const StatusCell = ({ row }: any) => {
  const theme = useTheme();
  const colors = theme.palette;

  return (
    <Chip
      sx={{
        minWidth: 64,
        color: row.isEnabled
          ? `${colors.boxColor.pgmGreen.color} !important`
          : `${colors.boxColor.redTrend.color} !important`,
        backgroundColor: row.isEnabled
          ? `${colors.boxColor.greenTrend.bgColor} !important`
          : `${colors.boxColor.redTrend.bgColor} !important`,
        marginTop: 0.5,
      }}
      label={row.isEnabled ? 'ACTIVE' : 'INACTIVE'}
    />
  );
};


export const statusFormatter = ({ row }: any) => {
  return <StatusCell row={row} />;
};


export const ChannelsTab = ({ channels, setChannels }: any) => {
    const [showChannelModal, setShowChannelModal] = useState(false);
    const defaultColumnNames = ['Template Name', 'Channel', 'Subject', 'Recipient', 'Status', 'Action'];
    const [selectedChannel, setSelectedChannel] = useState<ChannelRow|null>(null);

    const handleManageDeliveryChannel = (type: string, channelConfig: any) => {
        setSelectedChannel(null)
        if(type === 'Service Now'){
            channelConfig.payload = JSON.parse(JSON.stringify(channelConfig.payload))
        }
		setChannels({ ...channels, [type]: { ...channelConfig, channel: type } });
		setShowChannelModal(false);
	};

    function getColumns(): Array<Column<ChannelRow>> {
		return [
		  {
			key: "templateName",
			name: "Template Name",
			minWidth: 150,
			width: 308,
		  },
		  {
			key: "channel",
			name: "Channel",
			width: 200,
			minWidth: 150,
		  },
		  {
			key: "emailSubject",
			name: "Subject",
			minWidth: 95,
		  },
		  {
        key: "emailRecipients",
        name: "Recipient",
        minWidth: 120,
        formatter: recipientFormatter(),
      },
		  {
			key: "isEnabled",
			name: "Status",
			width: 150,
			formatter: statusFormatter,
		  },
		  {
			key: "action",
			name: "Action",
			formatter: actionFormatter(
				channels,
				setChannels,
				setSelectedChannel,
				setShowChannelModal
			),
			width: 80,
		  },
		];
    }
    return (
        <Box sx={{ mt: 3, padding: '20px', borderRadius: '8px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" sx={{ opacity: 0.7 }}  color="textLight.main">
                    ⓘ This section is to create multiple delivery channels for the alert.
                </Typography>
                <Button
					variant="outlined"
                    color="secondary"
                    onClick={() => setShowChannelModal(true)}
                    disabled={Object.keys(channels).length === 3}
                >
                    CREATE CHANNEL
                </Button>
            </Box>
            <CustomizedDataGrid
                tableColumns={getColumns()}
                tableData={Object.values(channels)}
                tableType="Alert"
                defaultColumnNames={defaultColumnNames}
                listLength={Object.keys(channels).length}
                isDateRangeFilter={false}
                showExport={false}
                isShowMoreButton={false}
                page={1}
                pageSize={0}
                paginationData={0}
            />
            <DialogueModal
                title={selectedChannel ? 'Edit Channel' : 'Create Channel'}
                isOpen={showChannelModal}
                handleClose={() => {
                    setShowChannelModal(false);
                    setSelectedChannel(null);
                }}
            >
                <ChannelModal
                    selectedChannel={selectedChannel}
                    channelOptions={['Email', 'Service Now', 'Teams'].filter(
                        (channel) => !Object.keys(channels).includes(channel)
                    )}
                    handleClose={() => {
                        setShowChannelModal(false);
                        setSelectedChannel(null);
                    }}
                    onAddChannel={handleManageDeliveryChannel}
                />
            </DialogueModal>
        </Box>
    )
}